import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import PropertyCard from "../components/common/PropertyCard";
import { projectsAPI, mapProjectToProperty, projectFirstImage } from "../services/api";

const GOLD = "#50C878";

export default function PropertiesPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "All");
  const [zoneFilter, setZoneFilter] = useState("All");
  const [areaFilter, setAreaFilter] = useState(searchParams.get("area") || "All");
  const [builderFilter, setBuilderFilter] = useState("All");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch projects from the backend (Supabase-backed)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await projectsAPI.list({ limit: 200 });
        if (!alive) return;
        const mapped = (res.data || []).map(mapProjectToProperty);
        setProjects(mapped);
        setError(null);

        // Replace stock images with each project's OWN image from its
        // Supabase Storage folder (nested Builder/Project/*.jpg). Resolve
        // every project individually so projects from the same builder
        // never share a photo — stock image stays as the fallback when a
        // project has no folder/images of its own.
        const entries = await Promise.all(
          mapped.map(async (p) => [
            p.id,
            await projectFirstImage(p.builderRaw, p.projectRaw),
          ])
        );
        if (!alive) return;
        const projectImg = new Map(entries);
        setProjects((prev) =>
          prev.map((p) => {
            const url = projectImg.get(p.id);
            return url ? { ...p, images: [url] } : p;
          })
        );
      } catch (err) {
        if (alive) setError(err.message || "Failed to load projects");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const s = searchParams.get("search");
    const a = searchParams.get("area");
    const t = searchParams.get("type");
    if (s) setSearch(s);
    if (a) setAreaFilter(a);
    if (t) setTypeFilter(t);
  }, [searchParams]);

  // Once projects load, normalize the type filter to the exact value that
  // exists in the data (e.g. URL "?type=apartment" → "Apartment"). This makes
  // the footer quick-links match regardless of casing/pluralization.
  useEffect(() => {
    if (typeFilter === "All" || !projects.length) return;
    const dataTypes = Array.from(new Set(projects.map((p) => p.type).filter(Boolean)));
    if (dataTypes.includes(typeFilter)) return;
    const want = typeFilter.toLowerCase().replace(/s$/, "");
    const match = dataTypes.find(
      (dt) => dt.toLowerCase() === want || dt.toLowerCase().replace(/s$/, "") === want
    );
    if (match) setTypeFilter(match);
  }, [projects, typeFilter]);

  const { types, zones, areas, builders } = useMemo(() => {
    const uniq = (arr) => ["All", ...Array.from(new Set(arr.filter(Boolean))).sort()];
    return {
      types: uniq(projects.map((p) => p.type)),
      zones: uniq(projects.map((p) => p.zone)),
      areas: uniq(projects.map((p) => p.area)),
      builders: uniq(projects.map((p) => p.builder)),
    };
  }, [projects]);

  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (p.title || "").toLowerCase().includes(q) ||
      (p.area || "").toLowerCase().includes(q) ||
      (p.builder || "").toLowerCase().includes(q) ||
      (p.type || "").toLowerCase().includes(q) ||
      (p.zone || "").toLowerCase().includes(q);
    const matchType = typeFilter === "All" || p.type === typeFilter;
    const matchZone = zoneFilter === "All" || p.zone === zoneFilter;
    const matchArea = areaFilter === "All" || p.area === areaFilter;
    const matchBuilder = builderFilter === "All" || p.builder === builderFilter;
    return matchSearch && matchType && matchZone && matchArea && matchBuilder;
  });

  function clearFilters() {
    setSearch("");
    setTypeFilter("All");
    setZoneFilter("All");
    setAreaFilter("All");
    setBuilderFilter("All");
  }

  const hasActiveFilters =
    search || typeFilter !== "All" || zoneFilter !== "All" || areaFilter !== "All" || builderFilter !== "All";

  return (
    <div style={{ background: "#F2F8F3", color: "#013220", minHeight: "100vh", paddingTop: "68px" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
          borderBottom: "1px solid rgba(80,200,120,0.15)",
          padding: "48px 24px 40px",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            GroupBuy Flats
          </span>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 600, color: "#013220", margin: "8px 0 8px" }}>
            All Properties
          </h1>
          <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter', sans-serif", fontSize: "0.95rem" }}>
            {loading ? "Loading premium projects…" : `${projects.length} premium projects across Hyderabad`}
          </p>
        </div>
      </div>

      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 280px" }}>
            <Search size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "rgba(1,50,32,0.62)" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, area, builder, zone..."
              style={{
                width: "100%",
                padding: "12px 16px 12px 42px",
                background: "rgba(80,200,120,0.10)",
                border: "1.5px solid rgba(80,200,120,0.25)",
                borderRadius: "8px",
                color: "#013220",
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.9rem",
                outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = GOLD)}
              onBlur={(e) => (e.target.style.borderColor = "rgba(80,200,120,0.25)")}
            />
          </div>

          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 20px",
              background: filtersOpen ? "rgba(80,200,120,0.15)" : "rgba(80,200,120,0.10)",
              border: `1.5px solid ${filtersOpen ? GOLD : "rgba(80,200,120,0.25)"}`,
              borderRadius: "8px",
              color: GOLD,
              cursor: "pointer",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: "0.88rem",
            }}
          >
            <SlidersHorizontal size={15} /> Filters
            {hasActiveFilters && (
              <span style={{ background: GOLD, color: "#FFFFFF", borderRadius: "50%", width: "18px", height: "18px", fontSize: "0.7rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                !
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "12px 16px", background: "transparent", border: "1.5px solid rgba(255,100,100,0.4)", borderRadius: "8px", color: "#e05a5a", cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: "0.85rem" }}
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>

        {filtersOpen && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "12px",
              padding: "20px",
              background: "rgba(80,200,120,0.05)",
              border: "1px solid rgba(80,200,120,0.2)",
              borderRadius: "8px",
              marginBottom: "24px",
            }}
          >
            {[
              { label: "Type", value: typeFilter, setter: setTypeFilter, options: types },
              { label: "Zone", value: zoneFilter, setter: setZoneFilter, options: zones },
              { label: "Area", value: areaFilter, setter: setAreaFilter, options: areas },
              { label: "Builder", value: builderFilter, setter: setBuilderFilter, options: builders },
            ].map(({ label, value, setter, options }) => (
              <div key={label}>
                <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.75rem", marginBottom: "6px", fontFamily: "'Inter', sans-serif", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  {label}
                </label>
                <select
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    background: "rgba(80,200,120,0.10)",
                    border: "1.5px solid rgba(80,200,120,0.25)",
                    borderRadius: "6px",
                    color: value === "All" ? "rgba(1,50,32,0.62)" : "#013220",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.88rem",
                    outline: "none",
                    cursor: "pointer",
                  }}
                >
                  {options.map((opt) => (
                    <option key={opt} value={opt} style={{ background: "#FFFFFF" }}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && (
          <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", marginBottom: "24px" }}>
            Showing <strong style={{ color: GOLD }}>{filtered.length}</strong> of {projects.length} projects
            {zoneFilter !== "All" && ` in ${zoneFilter} zone`}
            {areaFilter !== "All" && ` · ${areaFilter}`}
            {typeFilter !== "All" && ` · ${typeFilter}s`}
          </p>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <div style={{ width: "44px", height: "44px", border: "3px solid rgba(80,200,120,0.3)", borderTopColor: GOLD, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
            <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter', sans-serif", marginTop: "20px" }}>Loading projects…</p>
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", color: "#e05a5a" }}>Unable to load projects</p>
            <p style={{ color: "rgba(1,50,32,0.55)", fontFamily: "'Inter', sans-serif" }}>{error}</p>
          </div>
        )}

        {!loading && !error && (filtered.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "28px",
            }}
            className="props-grid"
          >
            {filtered.map((prop) => (
              <PropertyCard key={prop.id} property={prop} />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", color: "rgba(1,50,32,0.62)" }}>
              No projects found
            </p>
            <p style={{ color: "rgba(1,50,32,0.48)", fontFamily: "'Inter', sans-serif", marginBottom: "24px" }}>
              Try adjusting your search or filters
            </p>
            <button onClick={clearFilters} style={{ background: "linear-gradient(135deg, #50C878, #7FD9A0)", color: "#FFFFFF", border: "none", padding: "12px 28px", borderRadius: "6px", fontWeight: 700, cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>
              Clear Filters
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 640px) {
          .props-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}